import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
    SimpleChange,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogType,
} from '@components/confirmation-dialog/confirmation-dialog.component';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { SensorCableTagChannelsComponent } from '@components/sensor-cable-tag-channels/sensor-cable-tag-channels.component';
import { TranslateService } from '@ngx-translate/core';
import { MessageDialogService } from '@service/message-dialog.service';
import { SensorCableTagService } from '@service/sensor-cable-tag/sensor-cable-tag.service';
import { SensorTagSlot } from 'icreate/common/models/sensor-cable-tag';
import { emptySlot, emptyValue, SlotOptionType, TerminalType } from 'icreate/common/models/slot';
import { Vessel } from 'icreate/common/models/vessel';
import { lastValueFrom, Subscription, take } from 'rxjs';
import { FcuNumSelectorComponent } from './fcu-num-selector/fcu-num-selector.component';
import { PrintSensorCableService } from '@service/sensor-cable-tag/print-sensor-cable-tag.service';

type SelectorType = 'fcu' | 'fu' | 'slot' | 'output';
type SelectorOption = number | string;

@Component({
    selector: 'app-sensor-cable-tag',
    templateUrl: './sensor-cable-tag.component.html',
    styleUrl: './sensor-cable-tag.component.css',
    standalone: false,
})
export class SensorCableTagComponent implements OnInit, AfterViewInit, OnDestroy {
    readonly fcuSelector: SelectorType = 'fcu';
    readonly fuSelector: SelectorType = 'fu';
    readonly slotSelector: SelectorType = 'slot';
    readonly outputSelector: SelectorType = 'output';
    readonly NO_SELECTION = 'no-selection';

    private readonly selectedTranslationKeys: Record<Exclude<SelectorType, 'output'>, string> = {
        fcu: 'sensor-cable-tag.selected.fcu',
        fu: 'sensor-cable-tag.selected.fu',
        slot: 'sensor-cable-tag.selected.slot',
    };

    private readonly outputTranslationPrefix = 'sensor-cable-tag.selected.ouput';

    @Input() vessel!: Vessel;
    @Input() userCanEdit: boolean = true;
    @Output() pageUpdated = new EventEmitter<boolean>();

    @ViewChild('slotChannels') slotChannels: SensorCableTagChannelsComponent;
    @ViewChild('slotChangeConfirmation') slotChangeConfirmation: ConfirmationWindowComponent;
    @ViewChild('selectorElement') selectorElement: ElementRef<HTMLElement>;
    @ViewChild('excelInput') excelInput!: ElementRef<HTMLInputElement>;
    @ViewChild('importErrorDialog') importErrorDialog: ConfirmationWindowComponent;

    private vesselSlots: SensorTagSlot[];
    private settingsUpdatedSubscription: Subscription;

    private slotsVersion = 0;
    private optionsCache = new Map<SelectorType, { key: string; options: SelectorOption[] }>();

    isLoading: boolean = false;
    selectedExcelFcu: number | null = null;

    searchChannelInput: string;
    channelInput: string;

    openSelector: SelectorType | null = null;

    selectedFcuNum: number;
    selectedFuNum: number;
    selectedSlotNum: number;
    slotType: SlotOptionType;
    selectedOutput: TerminalType;

    selectedSlot: SensorTagSlot;

    importErrorMessage: string;

    constructor(
        private sensorCableTagService: SensorCableTagService,
        private translateService: TranslateService,
        private cdr: ChangeDetectorRef,
        private messageDialogService: MessageDialogService,
        private dialog: MatDialog,
        private sensorTagPrint: PrintSensorCableService
    ) {}

    @HostListener('document:click', ['$event'])
    clickOut(event: MouseEvent) {
        if (!this.openSelector) return;
        const clickedInsideSelector = this.selectorElement?.nativeElement.contains(event.target as Node);
        if (!clickedInsideSelector) this.closeSelectors();
    }

    async ngOnInit() {
        if (!this.vessel) return;
        try {
            this.isLoading = true;
            const slots: any[] = await this.sensorCableTagService.getVesselSlots(this.vessel.id);
            if (slots?.length > 0) {
                this.vesselSlots = slots.map(slot => ({
                    no: slot.no,
                    slotId: slot.id,
                    ioType: slot.ioType,
                    fcuNum: slot.FcuUnit?.index,
                    fuNum: slot.FuUnit?.index,
                    terminal: slot.terminal,
                    vesselId: this.vessel.id,
                }));
                this.slotsVersion++;
            }
        } catch (err) {
            console.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    ngAfterViewInit(): void {
        this.settingsUpdatedSubscription = this.sensorCableTagService.sensorCableTagsUpdated$().subscribe(vesselId => {
            if (vesselId === this.vessel?.id) {
                this.showMessageDialog();
            }
        });
    }

    ngOnDestroy(): void {
        this.settingsUpdatedSubscription?.unsubscribe();
    }

    isFuSelected(): boolean {
        return this.selectedFuNum !== undefined && this.selectedFuNum !== null;
    }

    shouldShowSelectMessage() {
        return !this.selectedFcuNum || !this.selectedSlotNum;
    }

    shouldShowNotFound() {
        return !this.shouldShowSelectMessage() && !this.selectedSlot;
    }

    emptySlotSelected() {
        return !!this.selectedSlot && this.selectedSlot.ioType === emptyValue;
    }

    shouldShowChannels() {
        return !!this.selectedSlot && this.selectedSlot.ioType !== emptyValue;
    }

    isDOSlot() {
        return !!this.selectedSlot && this.selectedSlot.ioType === 'DO';
    }

    tableHasChanges() {
        return !!this.slotChannels?.hasUnsavedChanges();
    }

    tableHasErrors() {
        return !!this.slotChannels?.tableHasErrors();
    }

    isSelectorDisabled(selector: SelectorType): boolean {
        switch (selector) {
            case 'fu':
                return !this.selectedFcuNum;
            case 'slot':
                return !this.selectedFcuNum || !this.isFuSelected();
            default:
                return false;
        }
    }

    toggleSelector(selector: SelectorType, event: Event) {
        event.stopPropagation();
        if (!this.userCanEdit || this.isSelectorDisabled(selector)) return;
        this.openSelector = this.openSelector === selector ? null : selector;
    }

    closeSelectors() {
        this.openSelector = null;
    }

    trackByOption = (_: number, option: SelectorOption) => option;

    getSelectorText(selector: SelectorType): string {
        const select = this.translate('sensor-cable-tag.filter.select');
        switch (selector) {
            case 'fcu':
                return this.selectedFcuNum
                    ? this.translate(this.selectedTranslationKeys.fcu, { num: this.selectedFcuNum })
                    : select;
            case 'fu':
                if (!this.isFuSelected()) return select;
                return this.selectedFuNum === 0
                    ? this.translate('sensor-cable-tag.filter.no-selection')
                    : this.translate(this.selectedTranslationKeys.fu, { num: this.selectedFuNum });
            case 'slot':
                return this.selectedSlotNum
                    ? this.translate(this.selectedTranslationKeys.slot, { num: this.selectedSlotNum })
                    : select;
            case 'output':
                return this.selectedOutput ? String(this.selectedOutput) : select;
        }
    }

    getOptionLabel(selector: SelectorType, option: SelectorOption): string {
        if (selector === 'output') return String(option);
        if (this.isNoSelectionOption(option)) return this.translate('sensor-cable-tag.filter.no-selection');
        return this.translate(this.selectedTranslationKeys[selector], { num: option });
    }

    isNoSelectionOption(option: SelectorOption) {
        return option === this.NO_SELECTION;
    }

    getSelectorOptions(selector: SelectorType): SelectorOption[] {
        const key = [this.slotsVersion, this.selectedFcuNum, this.selectedFuNum, this.selectedSlot?.slotId].join('|');

        const cached = this.optionsCache.get(selector);
        if (cached?.key === key) return cached.options;

        const options = this.computeSelectorOptions(selector);
        this.optionsCache.set(selector, { key, options });
        return options;
    }

    private computeSelectorOptions(selector: SelectorType): SelectorOption[] {
        if (!this.vesselSlots?.length) return [];

        if (selector === 'output') {
            return this.selectedSlot?.terminal?.length ? this.getOutputTypes() : [];
        }

        const key: keyof SensorTagSlot = { fcu: 'fcuNum', fu: 'fuNum', slot: 'no' }[selector] as keyof SensorTagSlot;

        const filterByFcu = selector !== 'fcu' && !!this.selectedFcuNum;
        const filterByFu = selector === 'slot' && this.isFuSelected();
        const fuValue = this.selectedFuNum || undefined;

        const values = this.vesselSlots
            .filter(slot => {
                if (slot.ioType === emptySlot || slot.ioType === emptyValue) return false;
                if (filterByFcu && slot.fcuNum !== this.selectedFcuNum) return false;
                if (filterByFu && slot.fuNum !== fuValue) return false;
                return true;
            })
            .map(slot => slot[key] as unknown as number)
            .filter(Boolean)
            .sort((a, b) => a - b);

        const options: SelectorOption[] = selector === 'fu' ? [this.NO_SELECTION, ...values] : values;
        return [...new Set(options)];
    }

    private getOutputTypes(): string[] {
        const terminals = this.selectedSlot.terminal;
        const counts: Record<string, number> = {};
        terminals.forEach(terminal => {
            counts[terminal.type] = (counts[terminal.type] || 0) + 1;
        });

        const letterIndex: Record<string, number> = {};

        return terminals.map(terminal => {
            const type = terminal.type;
            if (counts[type] === 1) return type;

            const translated = this.translate(this.outputTranslationPrefix.concat('.', type.toLowerCase()));
            const letter = String.fromCharCode(65 + (letterIndex[type] ?? 0));
            letterIndex[type] = (letterIndex[type] ?? 0) + 1;

            return `${translated} ${letter}`;
        });
    }

    async optionSelected(selector: SelectorType, option: SelectorOption) {
        this.closeSelectors();
        if (!this.vesselSlots?.length) return;

        if (this.tableHasChanges()) {
            const shouldContinue = await this.openChangeConfirmation();
            if (!shouldContinue) return;
        }

        if (selector === 'fcu') {
            this.selectedFcuNum = option as number;
            this.selectedFuNum = undefined;
            this.selectedSlotNum = null;
            this.selectedOutput = null;
        } else if (selector === 'fu') {
            this.selectedFuNum = option === this.NO_SELECTION ? 0 : (option as number);
            this.selectedSlotNum = null;
            this.selectedOutput = null;
        } else if (selector === 'slot') {
            this.selectedSlotNum = option as number;
            this.selectedOutput = null;
        } else if (selector === 'output') {
            this.selectedOutput = option as unknown as TerminalType;
        }
        this.updateSelectedSlot();
        this.cdr.detectChanges();
    }

    updateSelectedSlot() {
        if (!this.userCanEdit) return;
        if (this.shouldShowSelectMessage() || !this.vesselSlots?.length) {
            this.selectedSlot = null;
            this.slotType = null;
            this.selectedOutput = null;
            return;
        }

        const slot = this.vesselSlots.find(
            s =>
                this.selectedFcuNum === s.fcuNum &&
                (this.selectedFuNum || undefined) === s.fuNum &&
                this.selectedSlotNum === s.no
        );

        this.selectedSlot = slot ?? null;
        this.slotType = slot?.ioType ?? null;
    }

    async clearSelectors() {
        if (this.tableHasChanges()) {
            const shouldContinue = await this.openChangeConfirmation();
            if (!shouldContinue) return;
        }
        this.searchChannelInput = undefined;
        this.channelInput = undefined;

        this.closeSelectors();

        this.selectedFcuNum = null;
        this.selectedFuNum = undefined;
        this.selectedSlotNum = null;
        this.selectedSlot = null;
        this.slotType = null;
        this.selectedOutput = null;

        this.cdr.detectChanges();
    }

    searchChannel(channel: string) {
        this.searchChannelInput = channel;
        this.channelInput = channel;
    }

    async openChangeConfirmation() {
        if (!this.slotChangeConfirmation) return true;
        this.slotChangeConfirmation.setVisible(true);
        const confirmationOption = await lastValueFrom(this.slotChangeConfirmation.onOptionSelected.pipe(take(1)));
        if (confirmationOption === ConfirmationWindowTypes.GO_BACK) {
            return false;
        } else if (confirmationOption === ConfirmationWindowTypes.SAVE) {
            await this.save();
        }
        return true;
    }

    showMessageDialog() {
        this.messageDialogService.openMessage('sensor-cable-tag.updated.message');
    }

    async save() {
        if (!this.tableHasChanges() || this.tableHasErrors() || !this.userCanEdit) return;
        try {
            this.isLoading = true;
            await this.slotChannels.save();
        } catch (err) {
            console.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    onChannelsUpdated(value: boolean) {
        this.pageUpdated.emit(!this.tableHasErrors() && value);
    }

    async printSensorCableTable() {
        if (this.shouldShowChannels() && this.tableHasErrors()) return;

        if (this.tableHasChanges()) {
            const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
                width: '474px',
                data: {
                    type: ConfirmationDialogType.COMPLEX,
                    title: 'confirmation-dialog.print.title',
                    message: 'confirmation-dialog.print.message',
                    backButton: 'confirmation-dialog.print.back',
                    discardButton: 'confirmation-dialog.print.discard',
                    saveButton: 'confirmation-dialog.print.save',
                },
            });
            const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

            if (confirmationOption === ConfirmationWindowTypes.GO_BACK) {
                return;
            } else if (confirmationOption === ConfirmationWindowTypes.SAVE) {
                await this.save();
            }
        }
        try {
            await this.sensorTagPrint.triggerFCUPrint(this.vessel, true);
        } catch (err) {
            console.error('Error in printSensorCableTable:', err);
            throw err;
        }
    }

    private async pickFcu(isImport = false): Promise<number | null> {
        const fcuOptions = this.getSelectorOptions(this.fcuSelector) as number[];
        if (!fcuOptions?.length) return null;
        if (fcuOptions.length === 1) return fcuOptions[0];

        const dialogRef = this.dialog.open(FcuNumSelectorComponent, {
            data: { fcuOptions, isImport },
            width: '474px',
        });
        return (await lastValueFrom(dialogRef.afterClosed())) ?? null;
    }

    async importSensorCableTable() {
        if (this.isImportButtonDisabled) return;
        this.selectedExcelFcu = null;
        const fcu = await this.pickFcu(true);
        if (fcu === null) return;
        this.selectedExcelFcu = fcu;
        this.excelInput.nativeElement.click();
    }

    async excelFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file || !this.selectedExcelFcu) return;

        this.isLoading = true;
        try {
            await this.sensorCableTagService.importExcelFile(file, this.vessel.id, this.selectedExcelFcu);
            this.slotChannels?.ngOnChanges({
                selectedSlot: new SimpleChange(null, this.selectedSlot, false),
            });
        } catch (err: any) {
            console.error(err);
            this.importErrorMessage = this.sensorCableTagService.getImportErrorMessage(
                err?.error?.errorCode,
                err?.error?.properties
            );
            this.importErrorDialog.setVisible(true);
        } finally {
            this.isLoading = false;
            this.selectedExcelFcu = null;
            if (this.excelInput) this.excelInput.nativeElement.value = '';
        }
    }

    async exportSensorCableTable() {
        if (this.isExportButtonDisabled) return;
        const fcu = await this.pickFcu();
        if (fcu === null) return;

        try {
            this.isLoading = true;
            const vesselId = this.vessel.id;
            const blob = await this.sensorCableTagService.exportExcelFile(vesselId, fcu);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Sensor_cable-fcu-${fcu}-vessel-${vesselId}.xlsm`;
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(url), 0);
        } catch (err) {
            console.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    get isImportButtonDisabled() {
        return !this.userCanEdit || !this.getSelectorOptions(this.fcuSelector).length;
    }

    get isExportButtonDisabled() {
        return !this.getSelectorOptions(this.fcuSelector).length;
    }

    private translate(key: string, params?: object): string {
        return this.translateService.instant(key, params);
    }
}
